import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BRAND } from "@/data/content";
import { useLanguage, SUPPORTED_LOCALES, DEFAULT_LOCALE, LOCALE_SHORT, ui, withLocalePrefix, switchLocale } from "@/context/LanguageContext";
import useSettings from "@/hooks/useSettings";
import useMenuPages from "@/hooks/useMenuPages";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { locale } = useLanguage();
    const settings = useSettings();
    const menuPages = useMenuPages();
    const enabledLangs = SUPPORTED_LOCALES.filter((l) => settings.languages?.[l] !== false);

    const onHome = location.pathname === "/" || location.pathname === `/${locale}` || location.pathname === `/${locale}/`;
    const solid = scrolled || !onHome;

    const customNav = menuPages.map((p) => ({
        label: p.titles?.[locale] || p.titles?.fr || p.slug,
        href: withLocalePrefix(`/${p.slug}`, locale),
        isHash: false,
    }));

    const NAV = [
        { label: ui(locale, "nav_interior"), href: `${withLocalePrefix("/", locale)}#expertise`, isHash: true },
        { label: ui(locale, "nav_realisations"), href: `${withLocalePrefix("/", locale)}#realisations`, isHash: true },
        { label: ui(locale, "nav_matelas"), href: withLocalePrefix("/matelas", locale) },
        ...customNav,
        { label: ui(locale, "nav_know_how"), href: `${withLocalePrefix("/", locale)}#pourquoi`, isHash: true },
        { label: ui(locale, "nav_process"), href: `${withLocalePrefix("/", locale)}#process`, isHash: true },
        { label: ui(locale, "nav_contact"), href: `${withLocalePrefix("/", locale)}#contact`, isHash: true },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const changeLocale = (l) => {
        setLangOpen(false);
        navigate(switchLocale(location.pathname, l));
    };

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                solid
                    ? "bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-slate-200/70 py-3"
                    : "bg-transparent py-5"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between">
                <Link
                    to={withLocalePrefix("/", locale)}
                    data-testid="navbar-logo"
                    className="flex items-center gap-3"
                >
                    <img
                        src={BRAND.logo}
                        alt="Logo L'Atelier du meuble by RO Concept"
                        className={`transition-all duration-500 w-auto ${
                            solid
                                ? "h-16 md:h-24 lg:h-32"
                                : "h-20 md:h-28 lg:h-40"
                        }`}
                    />
                    <span className="sr-only">{BRAND.name} {BRAND.suffix}</span>
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {NAV.map((n) =>
                        n.isHash ? (
                            <a
                                key={n.href}
                                href={n.href}
                                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                                className={`link-underline text-sm tracking-wide font-medium transition-colors ${solid ? "text-[#1E3A5F]" : "text-white"}`}
                            >
                                {n.label}
                            </a>
                        ) : (
                            <Link
                                key={n.href}
                                to={n.href}
                                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                                className={`link-underline text-sm tracking-wide font-medium transition-colors ${solid ? "text-[#1E3A5F]" : "text-white"}`}
                            >
                                {n.label}
                            </Link>
                        ),
                    )}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    {/* Language switcher */}
                    {enabledLangs.length > 1 && (
                        <div className="relative">
                            <button
                                onClick={() => setLangOpen((v) => !v)}
                                data-testid="lang-switcher"
                                className={`text-xs uppercase tracking-[0.2em] px-3 py-2 border transition-colors inline-flex items-center gap-2 ${
                                    solid
                                        ? "border-[#1E3A5F]/25 text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
                                        : "border-white/60 text-white hover:bg-white hover:text-[#1E3A5F]"
                                }`}
                            >
                                <Globe size={13} strokeWidth={1.5} />
                                {LOCALE_SHORT[locale]}
                            </button>
                            {langOpen && (
                                <div className="absolute right-0 top-full mt-2 bg-white border border-slate-200 shadow-lg min-w-[120px] py-2">
                                    {enabledLangs.map((l) => (
                                        <button
                                            key={l}
                                            onClick={() => changeLocale(l)}
                                            data-testid={`lang-option-${l}`}
                                            className={`block w-full text-left text-xs uppercase tracking-[0.2em] px-4 py-2 transition-colors ${
                                                l === locale ? "text-[#DC2626]" : "text-[#1E3A5F] hover:bg-slate-100"
                                            }`}
                                        >
                                            {LOCALE_SHORT[l]}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <a
                        href={`${withLocalePrefix("/", locale)}#contact`}
                        data-testid="navbar-cta"
                        className={`text-sm font-medium tracking-wide px-8 py-4 inline-flex items-center gap-2 transition-colors duration-300 ${
                            solid
                                ? "bg-[#1E3A5F] text-white hover:bg-[#152a45]"
                                : "bg-white text-[#1E3A5F] hover:bg-[#FDFBF7]"
                        }`}
                    >
                        {ui(locale, "cta_quote")}
                    </a>
                </div>

                <button
                    onClick={() => setOpen((v) => !v)}
                    data-testid="navbar-menu-toggle"
                    className={`lg:hidden p-2 transition-colors ${solid ? "text-[#1E3A5F]" : "text-white"}`}
                    aria-label="Menu"
                >
                    {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:hidden bg-[#FDFBF7] border-t border-slate-200"
                        data-testid="mobile-menu"
                    >
                        <div className="px-6 py-8 flex flex-col gap-6">
                            {NAV.map((n) =>
                                n.isHash ? (
                                    <a
                                        key={n.href}
                                        href={n.href}
                                        onClick={() => setOpen(false)}
                                        className="text-lg font-serif text-[#1E3A5F]"
                                        data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                                    >
                                        {n.label}
                                    </a>
                                ) : (
                                    <Link
                                        key={n.href}
                                        to={n.href}
                                        onClick={() => setOpen(false)}
                                        className="text-lg font-serif text-[#1E3A5F]"
                                        data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                                    >
                                        {n.label}
                                    </Link>
                                ),
                            )}
                            {enabledLangs.length > 1 && (
                                <div className="flex gap-2 mt-2 pt-4 border-t border-slate-200">
                                    {enabledLangs.map((l) => (
                                        <button
                                            key={l}
                                            onClick={() => { setOpen(false); changeLocale(l); }}
                                            data-testid={`mobile-lang-${l}`}
                                            className={`text-xs uppercase tracking-[0.2em] px-4 py-2 border ${
                                                l === locale ? "border-[#DC2626] text-[#DC2626]" : "border-slate-300 text-[#1E3A5F]"
                                            }`}
                                        >
                                            {LOCALE_SHORT[l]}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <a
                                href={`${withLocalePrefix("/", locale)}#contact`}
                                onClick={() => setOpen(false)}
                                className="btn-primary text-sm self-start mt-2"
                                data-testid="mobile-cta"
                            >
                                {ui(locale, "cta_quote")}
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}

